import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import { getReferral, IReferral } from "../../Services/Referral";
import MemberReferralDetailTable from "./components/MemberReferralDetailTable";
import StaffReferralDetailTable from "./components/StaffReferralDetailTable";
import { apiErrorMessage } from "./referralFormat";
import { referralMemberPageClass, referralMemberTitleClass, referralPageClass, referralStaffCloseClass, referralStaffHeaderClass, referralStaffTitleClass, ReferralLoading, ReferralPanel } from "./referralSurface";

type Props = {
  basePath: string;
  variant: "member" | "staff";
};

export default function ReferralDetail({ basePath, variant }: Props) {
  const params = useParams();
  const referralId = params.referralId || params.id;
  const [row, setRow] = useState<IReferral | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const location = useLocation();

  useEffect(() => {
    if (variant !== "member") {
      return;
    }
    setPageMeta({
      title: "WSG Referrals & Collaborations",
      breadcrumbLabels: {
        [basePath]: "Firm Referrals",
        [location.pathname]: "Referral Detail",
      },
    });
    return () => {
      clearPageMeta();
    };
  }, [variant, basePath, location.pathname, setPageMeta, clearPageMeta]);

  useEffect(() => {
    if (!referralId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getReferral(referralId).then((loaded) => {
      if (!cancelled) {
        setRow(loaded);
        setError("");
      }
    }).catch((loadError) => {
      if (!cancelled) {
        setError(apiErrorMessage(loadError, "The referral could not be loaded."));
      }
    }).finally(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [referralId]);

  const collaboration = row?.relationshipType === "collaboration" || row?.relationshipType === "colaboration";
  const whenLabel = collaboration ? "Collaboration Date" : "Referral Date";
  const byLabel = collaboration ? "Collaboration Member" : "Referred By";
  const toLabel = collaboration ? "Collaborated With" : "Referred To";
  const heading = collaboration ? "COLLABORATION - DETAIL" : "REFERRAL - DETAIL";

  if (variant === "member") {
    return (
      <div className={referralMemberPageClass}>
        <h2 className={`${referralMemberTitleClass} !mb-5`}>{heading}</h2>
        {error && <p className="text-feedback-error-default">{error}</p>}
        {loading ? <ReferralLoading /> : row && (
          <ReferralPanel>
            <MemberReferralDetailTable row={row} whenLabel={whenLabel} byLabel={byLabel} toLabel={toLabel} />
          </ReferralPanel>
        )}
      </div>
    );
  }

  return (
    <div className={referralPageClass}>
      <div className={referralStaffHeaderClass}>
        <h2 className={referralStaffTitleClass}>Member Referral and Collaboration - Detail</h2>
        <Link className={referralStaffCloseClass} to={basePath}>Close</Link>
      </div>
      {error && <p className="text-feedback-error-default">{error}</p>}
      {loading ? <ReferralLoading /> : row && (
        <ReferralPanel>
          <StaffReferralDetailTable row={row} whenLabel={whenLabel} byLabel={byLabel} toLabel={toLabel} />
        </ReferralPanel>
      )}
    </div>
  );
}
